import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  CreditCard,
  Lock,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Mail,
  Phone,
  Clock,
  ArrowRight,
  Edit3
} from 'lucide-react';
import { payOrderBackend, getProductImageUrl } from '../services/api';

const Payment = ({ cartItems = [], onClearCart }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Retrieve Order Data from state or sessionStorage
  const [order, setOrder] = useState(() => {
    if (location.state?.order) return location.state.order;
    try {
      const saved = sessionStorage.getItem('pendingOrder');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return null;
  });

  const [selectedMethod, setSelectedMethod] = useState('card'); // 'card' | 'upi' | 'netbanking' | 'wallet' | 'bnpl'
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Card Form State
  const [cardNumber, setCardNumber] = useState('');
  const [cardHolder, setCardHolder] = useState(order?.shipping_address?.full_name || '');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [saveCard, setSaveCard] = useState(true);

  // Billing Address State — initialized from checkout order
  const hasCustomBilling = order?.billing_address && order?.shipping_address
    ? JSON.stringify(order.billing_address) !== JSON.stringify(order.shipping_address)
    : false;

  const [sameAsShipping, setSameAsShipping] = useState(!hasCustomBilling);
  const [billingForm, setBillingForm] = useState({
    full_name: order?.billing_address?.full_name || order?.shipping_address?.full_name || '',
    email: order?.guest_email || order?.user_email || '',
    phone: order?.guest_phone || order?.shipping_address?.phone || '',
    street_address: order?.billing_address?.street_address || '',
    city: order?.billing_address?.city || '',
    postal_code: order?.billing_address?.postal_code || '',
    country: order?.billing_address?.country || 'United Kingdom'
  });

  useEffect(() => {
    if (!order) {
      // If no order data exists, redirect back to cart/checkout
      navigate('/cart');
    }
  }, [order, navigate]);

  if (!order) return null;

  const items = Array.isArray(order.items)
    ? order.items
    : typeof order.items === 'string'
    ? JSON.parse(order.items)
    : [];

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (!sameAsShipping) {
      if (!billingForm.full_name?.trim() || !billingForm.street_address?.trim() || !billingForm.city?.trim() || !billingForm.postal_code?.trim()) {
        setErrorMsg('Please enter your full billing address details before proceeding.');
        return;
      }
    }

    setLoading(true);

    const billing_address = sameAsShipping
      ? order.shipping_address
      : billingForm;

    const paymentData = {
      payment_method: selectedMethod === 'card' ? 'Credit/Debit Card' : selectedMethod.toUpperCase(),
      billing_address,
      payment_details: {
        card_last4: cardNumber ? cardNumber.slice(-4) : '4242',
        card_holder: cardHolder
      }
    };

    const res = await payOrderBackend(order.id, paymentData);
    setLoading(false);

    if (res.success) {
      setSuccessMsg('Payment Successful! Redirecting to confirmation page...');
      if (onClearCart) onClearCart();
      sessionStorage.removeItem('pendingOrder');
      setTimeout(() => {
        navigate('/order-success', {
          state: {
            order: res.order || order,
            txnId: `TXN-${Math.floor(10000000 + Math.random() * 90000000)}`
          }
        });
      }, 1000);
    } else {
      setErrorMsg(res.message || 'Payment processing failed. Please try again.');
    }
  };

  return (
    <div style={{ background: '#fafbfc', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#0f172a', padding: '40px 24px 80px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
        
        {/* HEADER BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '32px' }}>
          <div>
            <h1 style={{ fontSize: '32px', fontWeight: 900, color: '#0f172a', margin: '0 0 6px 0', letterSpacing: '-0.02em' }}>
              Secure Payment
            </h1>
            <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0 }}>
              All transactions are secure and encrypted.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857', padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Lock size={14} />
              <span>SSL SECURED</span>
            </div>
            <div style={{ background: '#fafbfc', border: '1px solid #e2e8f0', color: '#64748b', padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <ShieldCheck size={14} color="#dc2626" />
              <span>256-bit Encryption</span>
            </div>
          </div>
        </div>

        {/* MAIN SPLIT SCREEN (LEFT GATEWAY FORM + RIGHT ORDER SUMMARY) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px', alignItems: 'flex-start' }}>
          
          {/* LEFT SECTION: PAYMENT METHOD ACCORDION + BILLING DETAILS */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            
            {/* PAYMENT METHODS ACCORDION */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '28px', boxShadow: '0 4px 16px rgba(0,0,0,0.02)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', marginBottom: '20px' }}>
                Choose a Payment Method
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                
                {/* OPTION 1: CREDIT / DEBIT CARD */}
                <div style={{ border: selectedMethod === 'card' ? '2px solid #dc2626' : '1px solid #e2e8f0', borderRadius: '16px', padding: '16px', background: selectedMethod === 'card' ? '#fff5f5' : '#ffffff', transition: 'all 0.2s ease' }}>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', margin: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input type="radio" name="payment_method" checked={selectedMethod === 'card'} onChange={() => setSelectedMethod('card')} style={{ accentColor: '#dc2626' }} />
                      <span style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a' }}>Credit / Debit Card</span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', fontSize: '11px', fontWeight: 900, color: '#64748b' }}>
                      <span style={{ background: '#1e3a8a', color: '#fff', padding: '2px 6px', borderRadius: '4px' }}>VISA</span>
                      <span style={{ background: '#ea580c', color: '#fff', padding: '2px 6px', borderRadius: '4px' }}>MC</span>
                      <span style={{ background: '#0284c7', color: '#fff', padding: '2px 6px', borderRadius: '4px' }}>AMEX</span>
                    </div>
                  </label>

                  {selectedMethod === 'card' && (
                    <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '14px', paddingTop: '16px', borderTop: '1px solid #fecaca' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 800, color: '#475569', marginBottom: '6px', display: 'block' }}>Card Number</label>
                        <input
                          type="text"
                          placeholder="1234 5678 9012 3456"
                          value={cardNumber}
                          onChange={(e) => setCardNumber(e.target.value)}
                          style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                        />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={{ fontSize: '12px', fontWeight: 800, color: '#475569', marginBottom: '6px', display: 'block' }}>Cardholder Name</label>
                          <input
                            type="text"
                            placeholder="John Doe"
                            value={cardHolder}
                            onChange={(e) => setCardHolder(e.target.value)}
                            style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}
                          />
                        </div>

                        <div>
                          <label style={{ fontSize: '12px', fontWeight: 800, color: '#475569', marginBottom: '6px', display: 'block' }}>Expiry Date</label>
                          <input
                            type="text"
                            placeholder="MM / YY"
                            value={expiry}
                            onChange={(e) => setExpiry(e.target.value)}
                            style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}
                          />
                        </div>

                        <div>
                          <label style={{ fontSize: '12px', fontWeight: 800, color: '#475569', marginBottom: '6px', display: 'block' }}>CVV</label>
                          <input
                            type="password"
                            placeholder="123"
                            maxLength={4}
                            value={cvv}
                            onChange={(e) => setCvv(e.target.value)}
                            style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: '#475569', cursor: 'pointer' }}>
                        <input type="checkbox" checked={saveCard} onChange={(e) => setSaveCard(e.target.checked)} style={{ accentColor: '#dc2626' }} />
                        <span>Save card for faster checkout</span>
                      </label>
                    </div>
                  )}
                </div>

                {/* OPTION 2: UPI */}
                <div style={{ border: selectedMethod === 'upi' ? '2px solid #dc2626' : '1px solid #e2e8f0', borderRadius: '16px', padding: '16px', background: selectedMethod === 'upi' ? '#fff5f5' : '#ffffff' }}>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', margin: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input type="radio" name="payment_method" checked={selectedMethod === 'upi'} onChange={() => setSelectedMethod('upi')} style={{ accentColor: '#dc2626' }} />
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a' }}>UPI / QR Code</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Pay using GPay, PhonePe, Paytm or BHIM</div>
                      </div>
                    </div>
                    <span style={{ fontSize: '11px', fontWeight: 900, color: '#16a34a', background: '#dcfce7', padding: '2px 8px', borderRadius: '4px' }}>INSTANT</span>
                  </label>
                </div>

                {/* OPTION 3: NET BANKING */}
                <div style={{ border: selectedMethod === 'netbanking' ? '2px solid #dc2626' : '1px solid #e2e8f0', borderRadius: '16px', padding: '16px', background: selectedMethod === 'netbanking' ? '#fff5f5' : '#ffffff' }}>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', margin: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input type="radio" name="payment_method" checked={selectedMethod === 'netbanking'} onChange={() => setSelectedMethod('netbanking')} style={{ accentColor: '#dc2626' }} />
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a' }}>Net Banking</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Pay using your preferred bank</div>
                      </div>
                    </div>
                  </label>
                </div>

                {/* OPTION 4: WALLETS */}
                <div style={{ border: selectedMethod === 'wallet' ? '2px solid #dc2626' : '1px solid #e2e8f0', borderRadius: '16px', padding: '16px', background: selectedMethod === 'wallet' ? '#fff5f5' : '#ffffff' }}>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', margin: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input type="radio" name="payment_method" checked={selectedMethod === 'wallet'} onChange={() => setSelectedMethod('wallet')} style={{ accentColor: '#dc2626' }} />
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a' }}>Wallets</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Pay using Paytm, Mobikwik, PhonePe</div>
                      </div>
                    </div>
                  </label>
                </div>

              </div>
            </div>

            {/* BILLING DETAILS CARD WITH SEPARATE BILLING ADDRESS TOGGLE */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '28px', boxShadow: '0 4px 16px rgba(0,0,0,0.02)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', marginBottom: '16px' }}>
                Billing Details
              </h3>

              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 700, color: '#0f172a', cursor: 'pointer', marginBottom: sameAsShipping ? '0' : '20px' }}>
                <input
                  type="checkbox"
                  checked={sameAsShipping}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setSameAsShipping(isChecked);
                    if (isChecked && order?.shipping_address) {
                      setBillingForm({
                        full_name: order.shipping_address.full_name || '',
                        email: order.guest_email || order.user_email || '',
                        phone: order.guest_phone || '',
                        street_address: order.shipping_address.street_address || '',
                        city: order.shipping_address.city || '',
                        postal_code: order.shipping_address.postal_code || '',
                        country: order.shipping_address.country || 'United Kingdom'
                      });
                    }
                  }}
                  style={{ accentColor: '#dc2626' }}
                />
                <span>Billing address is same as shipping address</span>
              </label>

              {!sameAsShipping && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 800, color: '#475569', marginBottom: '6px', display: 'block' }}>Full Name</label>
                      <input
                        type="text"
                        value={billingForm.full_name}
                        onChange={(e) => setBillingForm({ ...billingForm, full_name: e.target.value })}
                        style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 800, color: '#475569', marginBottom: '6px', display: 'block' }}>Email Address</label>
                      <input
                        type="email"
                        value={billingForm.email}
                        onChange={(e) => setBillingForm({ ...billingForm, email: e.target.value })}
                        style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 800, color: '#475569', marginBottom: '6px', display: 'block' }}>Billing Street Address</label>
                    <input
                      type="text"
                      value={billingForm.street_address}
                      onChange={(e) => setBillingForm({ ...billingForm, street_address: e.target.value })}
                      style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 800, color: '#475569', marginBottom: '6px', display: 'block' }}>City</label>
                      <input
                        type="text"
                        value={billingForm.city}
                        onChange={(e) => setBillingForm({ ...billingForm, city: e.target.value })}
                        style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 800, color: '#475569', marginBottom: '6px', display: 'block' }}>Postal Code</label>
                      <input
                        type="text"
                        value={billingForm.postal_code}
                        onChange={(e) => setBillingForm({ ...billingForm, postal_code: e.target.value })}
                        style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {errorMsg && (
                <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '10px', padding: '12px', color: '#dc2626', fontSize: '13px', fontWeight: 700, marginTop: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <AlertCircle size={16} />
                  <span>{errorMsg}</span>
                </div>
              )}

              {successMsg && (
                <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '10px', padding: '12px', color: '#047857', fontSize: '13px', fontWeight: 800, marginTop: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle2 size={16} />
                  <span>{successMsg}</span>
                </div>
              )}

              <button
                type="button"
                onClick={handlePaymentSubmit}
                disabled={loading}
                style={{
                  width: '100%',
                  background: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '16px',
                  fontSize: '15px',
                  fontWeight: 900,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  marginTop: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 8px 20px rgba(220,38,38,0.25)',
                  transition: 'all 0.2s ease'
                }}
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Processing Payment...</span>
                  </>
                ) : (
                  <>
                    <Lock size={18} />
                    <span>Continue to Payment</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '11.5px', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <ShieldCheck size={14} color="#dc2626" />
                <span>You will not be charged until you review your order on the next step.</span>
              </div>
            </div>

          </div>

          {/* RIGHT SECTION: ORDER SUMMARY SIDEBAR MATCHING PAYMENT.PNG */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '24px', boxShadow: '0 4px 16px rgba(0,0,0,0.02)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', margin: 0 }}>Order Summary</h3>
                <Link to="/cart" style={{ fontSize: '12.5px', color: '#dc2626', fontWeight: 800, textDecoration: 'none' }}>Edit Cart</Link>
              </div>

              {/* Items List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '20px' }}>
                {items.map((it, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '10px', background: '#fafbfc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px', flexShrink: 0 }}>
                        <img src={getProductImageUrl(it.image_url || it.image || '/uploads/panel_72led.jpg')} alt={it.name} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>{it.name || it.title}</div>
                        {Array.isArray(it.selected_addons) && it.selected_addons.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '2px' }}>
                            {it.selected_addons.map((addon, aIdx) => (
                              <span key={aIdx} style={{ fontSize: '9.5px', fontWeight: 700, color: '#dc2626', background: '#fef2f2', border: '1px solid #fee2e2', padding: '1px 5px', borderRadius: '4px' }}>
                                + {addon.addon_name || addon.name} (+£{Number(addon.addon_price || addon.price || 0).toFixed(2)})
                              </span>
                            ))}
                          </div>
                        )}
                        <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>Qty: {it.quantity || 1}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 900, color: '#0f172a' }}>
                      £{(parseFloat(it.price || 0) * (it.quantity || 1)).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Price Breakdown */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#475569', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Subtotal</span>
                  <span style={{ fontWeight: 800, color: '#0f172a' }}>£{parseFloat(order.subtotal || 0).toFixed(2)}</span>
                </div>
                {parseFloat(order.discount_amount || 0) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#16a34a', fontWeight: 800 }}>
                    <span>Discount ({order.coupon_code || 'Coupon'})</span>
                    <span>-£{parseFloat(order.discount_amount).toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Shipping</span>
                  <span style={{ fontWeight: 800, color: '#dc2626' }}>
                    {parseFloat(order.shipping_fee || 0) === 0 ? 'FREE' : `£${parseFloat(order.shipping_fee).toFixed(2)}`}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>VAT Tax</span>
                  <span style={{ fontWeight: 800, color: '#0f172a' }}>£{parseFloat(order.tax_amount || 0).toFixed(2)}</span>
                </div>
              </div>

              {/* Total Amount */}
              <div style={{ borderTop: '2px dashed #e2e8f0', paddingTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '15px', fontWeight: 900, color: '#0f172a' }}>Total</span>
                <span style={{ fontSize: '24px', fontWeight: 900, color: '#dc2626' }}>
                  £{parseFloat(order.total_amount || 0).toFixed(2)}
                </span>
              </div>
            </div>

            {/* TRUST BADGES */}
            <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '16px', padding: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Lock size={22} color="#047857" style={{ flexShrink: 0 }} />
              <div>
                <h4 style={{ fontSize: '13px', fontWeight: 900, color: '#047857', margin: '0 0 2px 0' }}>Your payment information is secure</h4>
                <p style={{ fontSize: '11.5px', color: '#065f46', margin: 0, lineHeight: 1.4 }}>We use industry-standard encryption to protect your data.</p>
              </div>
            </div>

            <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '16px', padding: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <ShieldCheck size={22} color="#dc2626" style={{ flexShrink: 0 }} />
              <div>
                <h4 style={{ fontSize: '13px', fontWeight: 900, color: '#0f172a', margin: '0 0 2px 0' }}>30 Day Money Back Guarantee</h4>
                <p style={{ fontSize: '11.5px', color: '#64748b', margin: 0, lineHeight: 1.4 }}>Not satisfied? Get a full refund within 30 days of delivery.</p>
              </div>
            </div>

            {/* NEED HELP */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px' }}>
              <h4 style={{ fontSize: '13.5px', fontWeight: 900, color: '#0f172a', margin: '0 0 10px 0' }}>Need Help?</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px', color: '#475569' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Mail size={14} color="#dc2626" />
                  <span>Info@infraedgeredlight.co.uk</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Phone size={14} color="#dc2626" />
                  <span>0121 455 6529</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Clock size={14} color="#dc2626" />
                  <span>Mon - Fri: 9AM - 6PM</span>
                </div>
              </div>
            </div>

          </div>

        </div>

      </div>
    </div>
  );
};

export default Payment;
